<template>
  <label class="form-label">
    {{ label }}
  </label>

  <div class="row g-2 align-items-stretch">
    <div class="col-12 col-md">
      <input
        :placeholder="placeholder"
        v-model.number="innerValue"
        type="number"
        :step="0.01"
        class="form-control"
        :disabled="disabled"
      />
    </div>

    <div v-if="!disabled" class="col-12 col-md-auto">
      <div class="d-flex flex-wrap gap-2">
        <div class="btn-group" role="group" aria-label="Quick percentage">
          <button
            v-for="p in percentageOptions"
            :key="p"
            type="button"
            class="btn btn-outline-primary"
            @click="applyPercent(p)"
          >
            {{ p }}%
          </button>
        </div>

        <div class="input-group" style="max-width: 120px">
          <input
            type="number"
            class="form-control"
            step="0.01"
            placeholder="%"
            v-model.number="customPercent"
            @blur="applyPercent()"
          />
          <span class="input-group-text">%</span>
        </div>
      </div>

      <small class="text-muted d-block mt-1">
        Mode: <strong>{{ calcMethodLabel }}</strong>
        <span v-if="costPriceNum <= 0" class="ms-2 text-danger">
          (cost is 0)
        </span>
      </small>
    </div>
  </div>
</template>

<script>
export default {
  name: "PricePercentSuggestor",
  props: {
    modelValue: {
      type: [Number, String],
      default: 0,
    },
    costPrice: {
      type: Number,
      required: true,
    },
    calcMethod: {
      type: String,
      default: "margin",
      validator: (v) => ["margin", "markup"].includes(v),
    },
    percentageOptions: {
      type: Array,
      default: () => [35, 40, 42, 45],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowEmpty: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: true,
    },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      customPercent: 0, // current % shown in the small input
      innerValue: this.modelValue, // local mirror of v-model
    };
  },
  computed: {
    costPriceNum() {
      return this.toNum(this.costPrice);
    },
    calcMethodLabel() {
      return this.calcMethod === "margin" ? "Margin %" : "Markup %";
    },
  },
  watch: {
    modelValue(nv) {
      this.innerValue = this.normalize(nv);
      this.syncPercentFromValues();
    },
    innerValue(nv) {
      // propagate changes from the text input upward
      this.$emit("update:modelValue", this.normalize(nv));
      this.$emit("change", this.normalize(nv));
      this.syncPercentFromValues();
    },
    costPrice() {
      this.syncPercentFromValues();
    },
    calcMethod() {
      this.syncPercentFromValues();
    },
  },
  mounted() {
    // initialize percent from starting values
    this.innerValue = this.normalize(this.modelValue);
    this.syncPercentFromValues();
  },
  methods: {
    normalize(val) {
      return this.allowEmpty && (val === "" || val == null)
        ? ""
        : this.toNum(val);
    },
    toNum(val) {
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    },
    round2(n) {
      return Math.round((n + Number.EPSILON) * 100) / 100;
    },
    // Compute price from cost & percent using selected method
    priceFrom(cost, pct) {
      if (pct === 0) return cost;
      if (this.calcMethod === "margin") {
        // Margin % = (Price - Cost) / Price
        // Price = Cost / (1 - m)
        const m = pct / 100;
        if (m >= 1) return cost; // guard
        return cost / (1 - m);
      } else {
        // Markup % = (Price - Cost) / Cost
        // Price = Cost * (1 + mu)
        return cost * (1 + pct / 100);
      }
    },
    // Infer % from cost & price using selected method
    percentFrom(cost, price) {
      if (cost <= 0 || price <= 0) return 0;
      if (this.calcMethod === "margin") {
        // margin% = (P - C)/P * 100
        return ((price - cost) / price) * 100;
      } else {
        // markup% = (P - C)/C * 100
        return ((price - cost) / cost) * 100;
      }
    },
    applyPercent(p) {
      if (typeof p !== "undefined") this.customPercent = this.toNum(p);
      const cost = this.costPriceNum;
      const pct = this.toNum(this.customPercent);
      if (cost <= 0 || pct === 0) {
        // still allow zero to set price == cost
        if (pct === 0 && cost > 0) {
          this.innerValue = this.round2(cost);
        }
        return;
      }
      const result = this.priceFrom(cost, pct);
      this.innerValue = this.round2(result);
    },
    syncPercentFromValues() {
      const cost = this.costPriceNum;
      const price = this.toNum(this.innerValue);
      if (price > 0 && cost > 0) {
        this.customPercent = this.round2(this.percentFrom(cost, price));
      } else {
        this.customPercent = 0;
      }
    },
  },
};
</script>
